import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { format } from 'date-fns';
import { BracketReview } from './bracket/BracketReview';

const BRACKET_FORMATS = ['single_elimination', 'double_elimination', 'consolation'];

export function ReviewStep({ eventData, setEventData, generatedGameTimes, draft }) {
  return (
    <div className="space-y-6 wizard-step">
      {BRACKET_FORMATS.includes(eventData.format) && <BracketReview eventData={eventData} setEventData={setEventData} draft={draft} />}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-bold text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>EVENT DETAILS</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex justify-between py-2 border-b border-slate-100">
            <span className="text-slate-500">Event Name</span>
            <span className="font-medium text-slate-800">{eventData.name}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-slate-100">
            <span className="text-slate-500">Start Date</span>
            <span className="font-medium text-slate-800">{format(eventData.start_date, 'PPP')}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-slate-100">
            <span className="text-slate-500">Timezone</span>
            <span className="font-medium text-slate-800">{eventData.timezone}</span>
          </div>
          {eventData.venue && (
            <div className="flex justify-between py-2 border-b border-slate-100">
              <span className="text-slate-500">Venue</span>
              <span className="font-medium text-slate-800">{eventData.venue}</span>
            </div>
          )}
          <div className="flex justify-between py-2">
            <span className="text-slate-500">Courts/Fields</span>
            <span className="font-medium text-slate-800">{eventData.num_courts}</span>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-bold text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>TEAMS ({eventData.teams.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {eventData.teams.map((team, i) => (
              <span key={team.id} className="px-3 py-1 bg-slate-100 rounded-full text-sm font-medium text-slate-700">{i + 1}. {team.name}</span>
            ))}
          </div>
        </CardContent>
      </Card>

      {eventData.pool_config.enabled && eventData.pool_config.pools.length > 0 && (
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-bold text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>POOLS ({eventData.pool_config.pools.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {eventData.pool_config.pools.map((pool) => {
                const poolTeams = eventData.teams.filter(t => pool.team_ids.includes(t.id));
                return (
                  <div key={pool.id} className="p-3 bg-slate-50 rounded-lg">
                    <h5 className="font-bold text-[#00247D] mb-2" style={{ fontFamily: 'Barlow Condensed' }}>{pool.name}</h5>
                    <div className="flex flex-wrap gap-1">
                      {poolTeams.map((team) => (
                        <span key={team.id} className="px-2 py-0.5 bg-white rounded text-xs font-medium text-slate-600">{team.name}</span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-bold text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>FORMAT & RULES</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex justify-between py-2 border-b border-slate-100">
            <span className="text-slate-500">Format</span>
            <span className="font-medium text-slate-800 capitalize">{eventData.format === 'consolation' ? '3-Game Guarantee' : eventData.format.replace(/_/g, ' ')}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-slate-100">
            <span className="text-slate-500">Games per Day</span>
            <span className="font-medium text-slate-800">{eventData.rules.games_per_day}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-slate-100">
            <span className="text-slate-500">Game Duration</span>
            <span className="font-medium text-slate-800">{eventData.rules.game_duration} min</span>
          </div>
          <div className="flex justify-between py-2 border-b border-slate-100">
            <span className="text-slate-500">Buffer Time</span>
            <span className="font-medium text-slate-800">{eventData.rules.buffer_time} min</span>
          </div>
          <div className="flex justify-between py-2 border-b border-slate-100">
            <span className="text-slate-500">Game Days</span>
            <span className="font-medium text-slate-800">{eventData.game_days.map(d => ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][d]).join(', ')}</span>
          </div>
          {eventData.blackout_dates && (
            <div className="flex justify-between py-2 border-b border-slate-100">
              <span className="text-slate-500">Blackout Dates</span>
              <span className="font-medium text-slate-800">{eventData.blackout_dates}</span>
            </div>
          )}
          <div className="flex justify-between py-2 border-b border-slate-100">
            <span className="text-slate-500">First Game Time</span>
            <span className="font-medium text-slate-800">{eventData.rules.first_game_time}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-slate-100">
            <span className="text-slate-500">Game Times</span>
            <span className="font-medium text-slate-800">{generatedGameTimes.join(', ')}</span>
          </div>
          <div className="flex justify-between py-2">
            <span className="text-slate-500">Home/Away</span>
            <span className={`font-medium ${eventData.rules.home_away_enabled ? 'text-green-600' : 'text-slate-500'}`}>
              {eventData.rules.home_away_enabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>
        </CardContent>
      </Card>

      {eventData.playoffs.enabled && (
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-bold text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>PLAYOFFS</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between py-2 border-b border-slate-100">
              <span className="text-slate-500">Teams Advancing</span>
              <span className="font-medium text-slate-800">Top {eventData.playoffs.teams_advance}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-slate-100">
              <span className="text-slate-500">Bracket Type</span>
              <span className="font-medium text-slate-800 capitalize">{eventData.playoffs.bracket_type} Elimination</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-slate-500">Seeding</span>
              <span className="font-medium text-slate-800 capitalize">{eventData.playoffs.seeding_method.replace('_', ' ')}</span>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
